import { buildWaiter } from '@ember/test-waiters';
import Modifier from 'ember-modifier';
import config from 'garaje/config/environment';
import Handsontable from 'handsontable';

const testing = config.environment === 'test';

const DEFAULT_OPTIONS = {
  manualColumnResize: true,
  minCols: 1,
  minRows: testing ? 7 : 50,
  minSpareRows: 1,
  rowHeaders: true,
  stretchH: 'all',
};

interface InstallHandsontableSignature {
  Args: {
    Positional: [
      Handsontable.DefaultSettings,
      Record<string, (...args: unknown[]) => unknown>,
      () => void | undefined,
      (instance: Handsontable) => void | undefined,
    ];
  };
  Element: HTMLElement;
}

export default class InstallHandsontable extends Modifier<InstallHandsontableSignature> {
  #hotInstance: Handsontable | null = null;
  #installedOptions?: string;

  modify(
    element: HTMLElement,
    [options, hooks, onUpdate, onInstall]: InstallHandsontableSignature['Args']['Positional'],
  ): void {
    // Prevent double initialization, instead call onUpdate closure
    if (element.className.includes('handsontable')) {
      // Update settings only if options changed to prevent infinite run loop
      if (JSON.stringify(options) !== this.#installedOptions) {
        this.#hotInstance?.updateSettings(options, false);
        this.#installedOptions = JSON.stringify(options);
        return onUpdate?.();
      }

      return;
    }

    const testWaiter = buildWaiter('handson-table-waiter');
    let token;
    if (testing) {
      token = testWaiter.beginAsync();
    }

    this.#installedOptions = JSON.stringify(options);
    this.#hotInstance = new Handsontable(element, { ...DEFAULT_OPTIONS, ...options });

    Object.keys(hooks).forEach((key) => {
      // @ts-expect-error - missing types for Handsontable.hooks.add?
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      Handsontable.hooks.add(
        key,
        (...args: unknown[]) => {
          const fn = hooks[key];
          if (typeof fn === 'function') {
            return fn(...[this.#hotInstance, ...args]);
          }
          return;
        },
        this.#hotInstance,
      );
    });

    onInstall?.(this.#hotInstance);

    if (testing) testWaiter.endAsync(token);
  }
}
